import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import * as styles from './styles';
import { cn } from '@cobliteam/ui/utils';
import moment from 'moment';

import { GeofenceCategory } from '@cobliteam/common-types';

import { isWebDataSuccess } from '@cobliteam/api-lib';

import { selectAddress } from '@cobliteam/store';

import { usePermissions } from '@cobliteam/hooks';

import { ContainerDefault, ContentLoader, DescriptionListDefault, DescriptionListMedium, DescriptionTitle } from '@cobliteam/base-components';
import IdleEngineCalibrationBadge from '@cobliteam/base-components/src/IdleEngineCalibrationBadge';

import Icon from '@cobliteam/ui/icons';

import TrackingPermissions from '../../permissions';
import type { ProcessedStop, ProcessedStopRouteStep } from '../../store';

export interface StopPopupProps {
  stop: ProcessedStop | ProcessedStopRouteStep;
  idleTimeHighlighted?: boolean;
  filteredDuration?: number;
  arrow?: boolean;
  children?: ReactNode;
}

const addressPlaceholder = () => (
  <ContentLoader width={250} height={14} className={styles.addressPlaceholder}>
    <rect x='0' y='0' width='100%' height='100%' />
  </ContentLoader>
);

export const StopPopup: React.FC<StopPopupProps> = ({ stop, children, idleTimeHighlighted, filteredDuration, arrow = true }) => {
  const { isAccessAllowed } = usePermissions();
  const isAllowedViewGeofences = isAccessAllowed(TrackingPermissions.VIEW_PLACES_OF_INTEREST);
  const addressWebData = useSelector(selectAddress);

  const stopNumber = stop.index + 1;
  const address = isWebDataSuccess(addressWebData) ? addressWebData.data : addressPlaceholder();

  const formatDuration = (valueInMillis: number): string => {
    const duration = moment.duration(valueInMillis);
    const hours = duration.days() * 24 + duration.hours();
    const hoursStr = hours ? `${hours}h ` : '';
    const minutesStr = duration.minutes() ? `${duration.minutes()}min ` : '';
    const secondsStr = duration.seconds() ? `${duration.seconds()}s` : '';
    return `${hoursStr}${minutesStr}${secondsStr}`;
  };

  const formatHour = (valueInMillis: number): string => moment(valueInMillis, 'x').format('HH:mm');

  const renderBadge = (style: string, label: string, icon: JSX.Element) => (
    <div className={cn(styles.badge, style)}>
      {icon}
      {label}
    </div>
  );

  const renderGeofenceBadge = (categoryStr?: string | null) => {
    switch (categoryStr) {
      case GeofenceCategory.BASE:
        return renderBadge(styles.badgeBase, 'Base', <Icon name='building' />);
      case GeofenceCategory.CLIENT:
        return renderBadge(styles.badgeClient, 'Cliente', <Icon name='user-round' />);
      case GeofenceCategory.OTHER:
        return renderBadge(styles.badgeOther, 'Outro', <Icon name='map-pin' />);
      default:
        return renderBadge(styles.badgeDefault, 'Tipo não cadastrado', <Icon name='map-pin' />);
    }
  };

  const getTitle = () => {
    const { startTime, endTime } = stop;

    return `Parada ${stopNumber} | ${formatHour(startTime)} às ${formatHour(endTime)}`;
  };

  const getGeofencesSubtitle = () => {
    if (stop.geofences.length === 1) {
      return `${stop.geofences[0].name}`;
    }
    return `Parada em ${stop.geofences.length} locais de interesse`;
  };

  const renderInfoRow = (label: string, value: string | JSX.Element) => (
    <DescriptionListDefault>
      <DescriptionListMedium>
        {label}
        :&nbsp;
      </DescriptionListMedium>
      {value}
    </DescriptionListDefault>
  );

  const idleEngineFilteredBadge = useMemo(() => <IdleEngineCalibrationBadge placement='top-start' />, []);

  const renderIdleEngineInfoRow = () => {
    if (stop.hasFilteredIdleOnTime) {
      return renderInfoRow('Motor ocioso', idleEngineFilteredBadge);
    }
    if (idleTimeHighlighted) {
      return (
        <div className={styles.idleHighlight}>
          <Icon name='triangle-alert' />
          {renderInfoRow('Motor ocioso', formatDuration(stop.idleOnInMillis))}
        </div>
      );
    }
    return renderInfoRow('Motor ocioso', formatDuration(stop.idleOnInMillis));
  };

  const stopDuration = stop.endTime - stop.startTime;

  return (
    <ContainerDefault className={cn(styles.popup, arrow ? styles.popupArrow : '')}>
      <div className={styles.titleWrapper}>
        <div className={styles.header}>
          <DescriptionTitle>{getTitle()}</DescriptionTitle>
          {isAllowedViewGeofences && stop.geofences.length === 1 && renderGeofenceBadge(stop.geofences[0].category)}
        </div>
        {stop.geofences.length > 0 && isAllowedViewGeofences && <DescriptionTitle className={styles.subTitle}>{getGeofencesSubtitle()}</DescriptionTitle>}
      </div>

      <div className={styles.body}>
        {renderInfoRow('Endereço', address)}
        {renderInfoRow('Duração', formatDuration(stopDuration))}
        {renderInfoRow('Motorista', stop.driverName || 'Sem motorista')}
        {renderIdleEngineInfoRow()}

        {filteredDuration && (
          <div className={styles.filteredHighlight}>
            <DescriptionListDefault>
              Você está vendo <b>{formatDuration(filteredDuration)}</b> do tempo total da parada
            </DescriptionListDefault>
          </div>
        )}
      </div>

      {children}
    </ContainerDefault>
  );
};
